import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { HEURES, CAPTATION_OPTIONS } from "@/lib/ficheSpectacle";
import { FicheTechniqueCard } from "@/components/FicheTechniqueCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Info, Save } from "lucide-react";

const Field = ({ label, children }) => (
  <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">{label}</div>{children}</div>
);
const Pick = ({ value, options, onChange, testid, className = "" }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger className={`h-8 text-xs ${className}`} data-testid={testid}><SelectValue placeholder="—" /></SelectTrigger>
    <SelectContent className="max-h-60">{options.map((o) => <SelectItem key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</SelectItem>)}</SelectContent>
  </Select>
);
const leadName = (l) => [l.prenom, l.nom].filter(Boolean).join(" ") || l.email;

export const InformationsGeneralesCard = ({ concert, onFicheChange }) => {
  const [infos, setInfos] = useState(concert.infos_generales || {});
  const [leads, setLeads] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/team-leads", { params: { role: "crew_boss" } }).then(({ data }) => setLeads(data)).catch(() => {}); }, []);
  const set = (k, v) => { setDirty(true); setInfos((i) => ({ ...i, [k]: v })); };
  const save = async () => {
    setSaving(true);
    try { await api.put(`/concerts/${concert.id}/fiche-spectacle`, { infos_generales: infos }); setDirty(false); toast.success("Informations générales enregistrées"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const sentAt = concert.team_request_crew_boss_at;

  return (
    <div className="rounded-2xl border border-border bg-card p-6 space-y-4" data-testid="informations-generales-card">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-display text-lg font-bold flex items-center gap-2"><Info className="h-5 w-5 text-primary" />Informations générales</h2>
        <Button size="sm" onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-infos-generales">
          <Save className="h-4 w-4 mr-1.5" />{saving ? "..." : dirty ? "Enregistrer *" : "Enregistrer"}
        </Button>
      </div>
      <FicheTechniqueCard embedded concertId={concert.id} fiche={concert.fiche_technique} onChange={onFicheChange} />
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <Checkbox checked={!!infos.plan_prevention} onCheckedChange={(v) => set("plan_prevention", !!v)} data-testid="infos-plan-prevention" />Plan de prévention
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Dossier devis"><Input className="h-8 text-xs" placeholder="Lien ou référence" value={infos.dossier_devis || ""} onChange={(e) => set("dossier_devis", e.target.value)} data-testid="infos-dossier-devis" /></Field>
        <Field label="Date envoi frais BB"><Input className="h-8 text-xs" type="date" value={infos.date_envoi_frais_bb || ""} onChange={(e) => set("date_envoi_frais_bb", e.target.value)} data-testid="infos-date-frais-bb" /></Field>
        <Field label="Crew boss"><Pick value={infos.crew_boss_id} options={leads.map((l) => ({ value: l.id, label: leadName(l) }))} onChange={(v) => set("crew_boss_id", v)} testid="infos-crew-boss" /></Field>
        <Field label="Demande d'équipe envoyée le">
          <div className="h-8 flex items-center text-xs" data-testid="infos-crew-boss-sent-at">{sentAt ? new Date(sentAt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "Non envoyée"}</div>
        </Field>
        <Field label="Demande captation vidéo"><Pick value={infos.captation} options={CAPTATION_OPTIONS} onChange={(v) => set("captation", v)} testid="infos-captation" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Traçage"><Pick value={infos.tracage} options={HEURES} onChange={(v) => set("tracage", v)} testid="infos-tracage" /></Field>
          <Field label="Load in"><Pick value={infos.load_in} options={HEURES} onChange={(v) => set("load_in", v)} testid="infos-load-in" /></Field>
        </div>
      </div>
    </div>
  );
};
